import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { PenLine, Plus, Search, Trash2 } from "lucide-react";
import DataTable from "../../../../widgets/DataTable";
import ideasServices from "../../../../services/ideasServices";
import CustomizableModal from "../../../../widgets/Modal/CustomizableModal";
import CustomSnackbar from "../../Snackbar/SnackBar";
import categoryServices from "../../../../services/categoryServices";

export default function CategoriesView() {
  const [categories, setCategories] = useState<any[]>([]);
  const [categoriesNames, setCategoriesNames] = useState<any[]>([]);
  const [visibleCategories, setVisibleCategories] = useState<Array<any>>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMode, setSnackbarMode] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<any | null>(null);
  const [categoriesTableData, setCategoriesTableData] = useState<Array<any>>(
    []
  );

  const handleSelectedCategoriesChange = (selected: any[]) => {
    setSelectedCategories(selected);
  };

  const unitsHeaders = [
    {
      name: "Nombre",
      id: "category",
    },
  ];

  const fetchCategories = () => {
    ideasServices
      .getAllCategories()
      .then((res) => {
        setCategories(res);
        const categoriesNames = res.map((cat: any) => cat.category);
        setCategoriesNames(categoriesNames);
      })
      .catch((err) => {
        console.error("Error al obtener los cambios de la idea", err);
        setCategories([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (visibleCategories.length > 0) {
      const updatedCategories = visibleCategories.map((category) => ({
        ...category,
        actions: [
          <IconButton
            key="edit"
            onClick={() => {
              setCategoryToEdit(category);
              setShowEditModal(true);
            }}
            disableRipple
          >
            <PenLine className="menu-actions__edit-icon" />
          </IconButton>,
        ],
      }));
      setCategoriesTableData(updatedCategories);
    }
  }, [visibleCategories]);

  useEffect(() => {
    const filtered = categories.filter((cat) => {
      const matchesCategories =
        !searchInput?.trim() ||
        cat.category.toLowerCase().includes(searchInput.toLowerCase());

      return matchesCategories;
    });
    setVisibleCategories(filtered);
  }, [searchInput, categories]);

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flex: "9" }} className="header__searching__search-bar">
          <Box>
            <Search className="header__searching__search-bar__search-icon" />
          </Box>
          <Autocomplete
            freeSolo
            forcePopupIcon={false}
            fullWidth
            options={categoriesNames}
            inputValue={searchInput}
            onInputChange={(e, newInput, reason) => {
              if (reason !== "reset") {
                setSearchInput(newInput);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar" />
            )}
          />
        </Box>
        <Box className="menu-actions">
          <IconButton onClick={() => setShowAddModal(true)} disableRipple>
            <Plus className="menu-actions__add-icon" />
          </IconButton>
          <IconButton
            disabled={selectedCategories?.length === 0}
            onClick={() => setShowDeleteModal(true)}
            disableRipple
          >
            <Trash2 className="menu-actions__trash-icon" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ marginTop: "20px" }}>
        <DataTable
          rows={categoriesTableData}
          headers={unitsHeaders}
          isLoading={isLoading}
          tableType="checkbox"
          onSelectionChange={handleSelectedCategoriesChange}
        />
      </Box>
      <CustomizableModal
        isOpen={showAddModal}
        nextButton="Guardar"
        modalType="add"
        panelView="category"
        options={categoriesNames}
        cancel={() => setShowAddModal(false)}
        save={(value) => {
          if (value?.trim()) {
            setShowAddModal(false);
            setIsModalLoading(true);
            categoryServices
              .createCategory({ name: value })
              .then(() => {
                setIsModalLoading(false);
                fetchCategories();
              })
              .finally(() => {
                setSnackbarMode("add");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showEditModal}
        initialValue={categoryToEdit?.category}
        nextButton="Guardar"
        modalType="edit"
        options={categoriesNames}
        panelView="category"
        cancel={() => setShowEditModal(false)}
        save={(newName) => {
          if (categoryToEdit && newName?.trim()) {
            setShowEditModal(false);
            setIsModalLoading(true);
            categoryServices
              .editCategory(categoryToEdit.id, { name: newName })
              .then(() => {
                setCategoryToEdit(null);
                setIsModalLoading(false);
                fetchCategories();
              })
              .finally(() => {
                setSnackbarMode("edit");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showDeleteModal}
        nextButton="Sí, eliminar"
        modalType="delete"
        panelView="category"
        cancel={() => setShowDeleteModal(false)}
        save={() => {
          if (selectedCategories?.length > 0) {
            setShowDeleteModal(false);
            setIsModalLoading(true);
            Promise.all(
              selectedCategories.map((category) =>
                categoryServices.deactivateCategory(category.id)
              )
            )
              .then(() => {
                setSelectedCategories([]);
                setIsModalLoading(false);
                fetchCategories();
              })
              .finally(() => {
                setSnackbarMode("delete");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        menu="categories"
        mode={snackbarMode}
      />
      {isModalLoading && (
        <Box className="loader-overlay">
          <Box className="loader-container">
            <Box className="custom-loader" />
          </Box>
        </Box>
      )}
    </Box>
  );
}
